import PreviewMedia from '../../widgets/components/PreviewMedia';
import { indexAfterRemoval } from '../../shared/utils/media';
import {
    Box,
    Button,
    Typography,
    TextField,
    FormControlLabel,
    Checkbox,
    IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSwipeable } from 'react-swipeable';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

const seasonOptions = ['Зима', 'Лето'];

const CreateTyrePage = () => {
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: '',
        brand: '',
        model: '',
        width: '',
        height: '',
        diameter: '',
        type: '',
        season: '',
        speedIndex: '',
        loadIndex: '',
        thorn: false,
        runflat: false,
        stock: '',
        price: '',
        price_rrp: '',
        article: '',
        isMy: false,
        bestseller: false
    });

    const [errors, setErrors] = useState<{ name?: string }>({});
    const [previews, setPreviews] = useState<string[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [files, setFiles] = useState<File[]>([]);
    const [previewIndex, setPreviewIndex] = useState(0);

    const handleChange = (field: string, value: string | boolean) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
            setFiles((prev) => [...prev, ...newFiles]);
            setPreviews((prev) => [...prev, ...newPreviews]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviews((prev) => prev.filter((_, i) => i !== index));
        setPreviewIndex(current => indexAfterRemoval(current, index, previews.length));
        setSelectedIndex(current => indexAfterRemoval(current, index, previews.length));
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            if (selectedIndex < previews.length - 1) {
                setSelectedIndex(selectedIndex + 1);
            }
        },
        onSwipedRight: () => {
            if (selectedIndex > 0) {
                setSelectedIndex(selectedIndex - 1);
            }
        },
        trackMouse: true,
    });

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            setErrors({ name: 'Наименование обязательно' });
            return;
        }

        const formData = new FormData();

        Object.entries(form).forEach(([key, value]) => {
            formData.append(key, String(value));
        });

        formData.append('previewIndex', String(previewIndex));
        files.forEach((file) => formData.append('media', file));

        try {
            await api.post('/api/admin/tyre', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            navigate('/tyre');
        } catch (err) {
            console.error('Ошибка при создании шины:', err);
        }
    };

    return (
        <Box p={4}>
            <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
                ← Назад
            </Button>
            <Typography variant="h5" gutterBottom>
                Создание новой шины
            </Typography>

            <Box component="form" sx={{ display: 'grid', gap: 2, maxWidth: 600 }}>
                <TextField
                    label="Наименование"
                    value={form.name}
                    onChange={(e) => {
                        setErrors({});
                        handleChange('name', e.target.value);
                    }}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    fullWidth
                    required
                />

                <TextField
                    label="Бренд"
                    value={form.brand}
                    onChange={(e) => handleChange('brand', e.target.value)}
                    fullWidth
                />

                <TextField
                    label="Модель"
                    value={form.model}
                    onChange={(e) => handleChange('model', e.target.value)}
                    fullWidth
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    <TextField
                        label="Ширина"
                        value={form.width}
                        onChange={(e) => handleChange('width', e.target.value)}
                    />
                    <TextField
                        label="Высота"
                        value={form.height}
                        onChange={(e) => handleChange('height', e.target.value)}
                    />
                    <TextField
                        label="Диаметр"
                        value={form.diameter}
                        onChange={(e) => handleChange('diameter', e.target.value)}
                    />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                        label="Индекс скорости"
                        value={form.speedIndex}
                        onChange={(e) => handleChange('speedIndex', e.target.value)}
                    />
                    <TextField
                        label="Индекс нагрузки"
                        value={form.loadIndex}
                        onChange={(e) => handleChange('loadIndex', e.target.value)}
                    />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                        label="Тип (Зима/Лето)"
                        value={form.type}
                        onChange={(e) => handleChange('type', e.target.value)}
                    />
                    <TextField
                        select
                        SelectProps={{ native: true }}
                        label="Сезон"
                        value={form.season}
                        onChange={(e) => handleChange('season', e.target.value)}
                    >
                        <option value=""></option>
                        {seasonOptions.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </TextField>
                </Box>

                <Box display="flex" gap={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.thorn}
                                onChange={(e) => handleChange('thorn', e.target.checked)}
                            />
                        }
                        label="Шипованные"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.runflat}
                                onChange={(e) => handleChange('runflat', e.target.checked)}
                            />
                        }
                        label="RunFlat"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.bestseller}
                                onChange={(e) => handleChange('bestseller', e.target.checked)}
                            />
                        }
                        label="Хит продаж"
                    />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                        label="Остаток"
                        type="number"
                        value={form.stock}
                        onChange={(e) => handleChange('stock', e.target.value)}
                    />
                    <TextField
                        label="Артикул"
                        value={form.article}
                        onChange={(e) => handleChange('article', e.target.value)}
                    />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                        label="Цена"
                        type="number"
                        value={form.price}
                        onChange={(e) => handleChange('price', e.target.value)}
                    />
                    <TextField
                        label="РРЦ"
                        type="number"
                        value={form.price_rrp}
                        onChange={(e) => handleChange('price_rrp', e.target.value)}
                    />
                </Box>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={form.isMy}
                            onChange={(e) => handleChange('isMy', e.target.checked)}
                        />
                    }
                    label="Моя шина"
                />

                <Button variant="contained" component="label">
                    Загрузить фото
                    <input type="file" hidden multiple onChange={handleFileChange} />
                </Button>

                {previews.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                            position: 'relative',
                            mt: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} {...swipeHandlers}>
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
                                }
                                disabled={selectedIndex === 0}
                            >
                                ←
                            </Button>

                            <Box
                                sx={{
                                    position: 'relative',
                                    width: 300,
                                    height: 200,
                                    overflow: 'hidden',
                                    borderRadius: 2,
                                }}
                            >
                                <PreviewMedia
                                    src={previews[selectedIndex]}
                                    mimeType={files[selectedIndex]?.type}
                                    alt={`preview-${selectedIndex}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        display: 'block',
                                        border:
                                            selectedIndex === previewIndex
                                                ? '3px solid #1976d2'
                                                : '1px solid #ccc',
                                        borderRadius: 8,
                                    }}
                                />

                                <IconButton
                                    onClick={() => handleRemoveFile(selectedIndex)}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        bgcolor: 'white',
                                        color: 'red',
                                        '&:hover': { bgcolor: '#ffe5e5' },
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            <Button
                                variant="outlined"
                                onClick={() =>
                                    setSelectedIndex((prev) =>
                                        prev < previews.length - 1 ? prev + 1 : prev
                                    )
                                }
                                disabled={selectedIndex === previews.length - 1}
                            >
                                →
                            </Button>
                        </Box>

                        <Box display="flex" gap={2} alignItems="center">
                            <Typography variant="caption">
                                {selectedIndex + 1} / {previews.length}
                            </Typography>

                            <Button
                                variant={selectedIndex === previewIndex ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => setPreviewIndex(selectedIndex)}
                            >
                                {selectedIndex === previewIndex
                                    ? 'Главное изображение'
                                    : 'Сделать главным'}
                            </Button>
                        </Box>
                    </Box>
                )}

                <Button variant="contained" color="primary" onClick={handleSubmit}>
                    Создать шину
                </Button>
            </Box>
        </Box>
    );
};

export default CreateTyrePage;
